import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    inject
} from '@angular/core';
import { PFieldBase } from '@panaya/ui/p-field';
import { ValueAccessorDirective } from '@panaya/ui/p-value-accessor';
import { TranslateService } from '@ngx-translate/core';
import { getAutomationId } from '@panaya/utils';

@Component({
    selector: 'p-field-outline-text',
    template: `
        <p-field
            [isMandatory]="isMandatory"
            [label]="label"
            [labelTooltip]="labelTooltip"
            [isReadOnly]="isReadOnly"
            [isDisable]="isDisabled"
            [shouldShowLabel]="!showLabelOnTop"
        >
            <mat-form-field
                class="p-input-outline"
                appearance="outline"
                slot="editMode"
                [ngClass]="{ 'p-input-readonly': isReadOnly, 'no-error-message': !errorMessage }"
                floatLabel="always"
            >
                <mat-label *ngIf="showLabelOnTop" class="p-input-text-label">{{ label }}</mat-label>
                <button
                    *ngIf="isSearch && isPrefixIcon"
                    matPrefix
                    aria-label="Search"
                    class="p-input-outline-button"
                    (click)="this.search.emit(valueAccessor.value)"
                >
                    <p-icon class="p-input-outline-icon-search" name="search" size="sm"></p-icon>
                </button>

                <input
                    [attr.automation-id]="automationId"
                    matInput
                    [matTooltip]="inputTooltip"
                    #inputField
                    type="text"
                    [value]="valueAccessor.value ?? ''"
                    [placeholder]="placeholder | sentenceCase"
                    [readonly]="isReadOnly"
                    [attr.aria-label]="ariaLabel"
                    autocomplete="off"
                    (input)="changeValue($any($event)?.target?.value)"
                    (keyup)="onKeyUp($event)"
                    onfocus="this.select()"
                    class="p-input-text-input"
                />

                <div *ngIf="!!showMatchesIndication" class="p-search-match-indication">
                    <span class="p-search-match-indication-current"> {{ currentMatchIndex }}</span>
                    /
                    <span> {{ totalMatchesCount }} </span>
                </div>

                <button
                    *ngIf="isNavigationSearch"
                    matSuffix
                    aria-label="Next"
                    class="p-input-outline-button"
                    (click)="navigationNextClicked.emit()"
                >
                    <p-icon class="p-input-outline-icon-up" name="chevron-up" size="sm"> </p-icon>
                </button>
                <button
                    *ngIf="isNavigationSearch"
                    matSuffix
                    aria-label="Prev"
                    class="p-input-outline-button"
                    (click)="navigationPrevClicked.emit()"
                >
                    <p-icon class="p-input-outline-icon-down" name="chevron-down" size="sm"> </p-icon>
                </button>

                <button
                    *ngIf="isSearch && valueAccessor.value && showExactMatchOption"
                    matSuffix
                    class="p-input-outline-button exact-match"
                    [ngClass]="{ selected: isExactMatch }"
                    (click)="isExactMatchClicked()"
                >
                    <p-icon
                        [matTooltip]="iconToolTip"
                        [matTooltipPosition]="'above'"
                        class="p-input-outline-icon-clear"
                        name="crosshair"
                        size="sm"
                    ></p-icon>
                </button>
                <button
                    *ngIf="isSearch && valueAccessor.value && !isPrefixIcon && isShowClearIcon"
                    matSuffix
                    aria-label="Clear"
                    class="p-input-outline-button"
                    (click)="clearValue()"
                >
                    <p-icon class="p-input-outline-icon-clear" name="x" size="sm"></p-icon>
                </button>

                <button
                    *ngIf="isSearch && !valueAccessor.value && !isPrefixIcon && isShowSearchIcon"
                    matSuffix
                    aria-label="Search"
                    class="p-input-outline-button"
                    [disabled]="!enableSearchAll"
                    (click)="this.search.emit(valueAccessor.value)"
                >
                    <p-icon class="p-input-outline-icon-search" name="search" size="sm"></p-icon>
                </button>

                <p-icon
                    *ngIf="iconName && !isSearch"
                    class="p-input-outline-icon"
                    matSuffix
                    [name]="iconName"
                    size="sm"
                    color="base-disabled"
                >
                </p-icon>

                <mat-error *ngIf="isValid" class="p-input-text-error">
                    {{ errorMessage }}
                </mat-error>
            </mat-form-field>
            <mat-form-field
                class="p-input-text"
                slot="readMode"
                appearance="fill"
                [ngClass]="{ 'p-input-text-readonly': isReadOnly }"
                floatLabel="always"
            >
                <input
                    [attr.automation-id]="automationId"
                    readonly
                    [disabled]="isDisabled"
                    matInput
                    type="text"
                    [value]="valueAccessor.value ?? ''"
                    [placeholder]="placeholder | sentenceCase"
                    [attr.aria-label]="ariaLabel"
                    class="p-input-text-input"
                />
            </mat-form-field>
        </p-field>
    `,
    styleUrls: ['./p-field-outline-text.component.scss'],
    hostDirectives: [ValueAccessorDirective],
    exportAs: 'pFieldOutlineText',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PFieldOutlineTextComponent extends PFieldBase<string> implements OnInit, AfterViewInit {
    private readonly cd = inject(ChangeDetectorRef);
    protected readonly translateService = inject(TranslateService);

    @Input() label?: string;
    @Input() ariaLabel?: string;
    @Input() placeholder?: string;
    @Input() errorMessage?: string;
    @Input() isReadOnly = false;
    @Input() isDisabled = false;
    @Input() isValid = true;
    @Input() labelTooltip: string = '';
    @Input() isMandatory = false;
    @Input() isSearch = false;
    @Input() isShowSearchIcon = true;
    @Input() isNavigationSearch = false;
    @Input() enableSearchAll = true;
    @Input() isPrefixIcon = false;
    @Input() isServerSideSearch = false;
    @Input() iconName?: string;
    @Input() autoFocus = false;
    @Input() searchDebounceTime: number = 300;
    @Input() showMatchesIndication: boolean = false;
    @Input() currentMatchIndex: number | undefined = undefined;
    @Input() totalMatchesCount: number | undefined = undefined;
    @Input() showLabelOnTop = false;
    @Input() inputTooltip: string = '';
    @Input() showExactMatchOption: boolean = false;
    @Input() isShowClearIcon: boolean = true;

    @Output() search: EventEmitter<any> = new EventEmitter();
    @Output() clearSearch: EventEmitter<any> = new EventEmitter();
    @Output() navigationNextClicked: EventEmitter<void> = new EventEmitter();
    @Output() navigationPrevClicked: EventEmitter<void> = new EventEmitter();
    @Output() exactMatchClicked: EventEmitter<boolean> = new EventEmitter();

    @ViewChild('inputField') inputField: ElementRef = {} as ElementRef;

    protected isExactMatch = false;
    protected iconToolTip = this.translateService.instant('Actions.exactMatch');

    ngOnInit(): void {
        if (!this.placeholder && this.isSearch) {
            this.placeholder = this.translateService.instant('Actions.search');
        }
    }

    ngAfterViewInit() {
        if (this.autoFocus) {
            this.focus();
            this.cd.detectChanges();
        }
    }

    focus() {
        this.inputField?.nativeElement.focus();
    }

    changeValue(value: any) {
        this.valueAccessor.markAsTouched();
        if (!this.isReadOnly) {
            this.valueAccessor.setValue(value);
        }
    }

    onKeyUp(event: any) {
        if (this.isSearch) {
            if ((event.key === 'Enter' || !this.isServerSideSearch) && (event.target?.value || this.enableSearchAll)) {
                this.search.emit(event.target?.value);
            }
        }
        if (event.target?.value?.length === 0) {
            this.isExactMatch = false;
        }
    }

    clearValue(): void {
        this.changeValue('');
        this.cd.detectChanges();
        if (this.isSearch) {
            this.clearSearch.emit('');
        }
        this.isExactMatch = false;
    }

    get automationId() {
        return getAutomationId('p-field-outline-text', this.label);
    }

    protected isExactMatchClicked() {
        this.isExactMatch = !this.isExactMatch;
        this.exactMatchClicked.emit(this.isExactMatch);
    }
}